import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, XCircle, CheckCircle, Clock, ArrowLeft } from "@phosphor-icons/react";
import { API_BASE } from "@/lib/api";

/**
 * Public verification page — no auth required.
 * Reached via the QR code on the printed certificate PDF.
 * Shows: course title, recipient, valid/invalid stamp, and a live rotating HMAC code
 * so teachers can confirm the printed code matches.
 */
export default function VerifyPage() {
  const [params] = useSearchParams();
  const courseId = params.get("course_id");
  const principalId = params.get("principal_id");
  const certId = params.get("cert_id");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [now, setNow] = useState(new Date());
  const [serverOffset, setServerOffset] = useState(0);

  const fetchOnce = async () => {
    try {
      const url = `${API_BASE}/public/certificate/verify?course_id=${encodeURIComponent(courseId)}&principal_id=${encodeURIComponent(principalId)}${certId ? `&cert_id=${encodeURIComponent(certId)}` : ""}`;
      const res = await fetch(url);
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || "Verification failed");
      }
      const d = await res.json();
      setData(d);
      setServerOffset(d.server_time_unix * 1000 - Date.now());
      return d;
    } catch (e) { setErr(e.message); }
  };

  useEffect(() => {
    if (!courseId || !principalId) { setErr("Missing course_id or principal_id in URL"); return; }
    let cancel = false;
    let timer;
    const loop = async () => {
      if (cancel) return;
      const d = await fetchOnce();
      if (cancel) return;
      const next = d?.next_rotation_in ? (d.next_rotation_in + 1) * 1000 : 30000;
      timer = setTimeout(loop, next);
    };
    loop();
    return () => { cancel = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line
  }, [courseId, principalId]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const liveTime = new Date(Date.now() + serverOffset);
  const secsLeft = data ? Math.max(0, 30 - (Math.floor(liveTime.getTime() / 1000) % 30)) : null;

  return (
    <div className="bg-grain min-h-screen grid place-items-center p-5">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="card max-w-lg w-full text-center"
        data-testid="public-verify-card"
      >
        <ShieldCheck size={56} weight="duotone" color="#CCFF00" className="mx-auto" />
        <div className="text-xs uppercase tracking-[0.3em] text-[#000000] mt-2 font-display">Certificate verification</div>

        {err && (
          <div className="mt-5 text-[#FF5100]" data-testid="verify-error">
            <XCircle size={48} weight="fill" className="mx-auto mb-2" />
            <div className="font-display font-bold text-xl">Couldn't verify</div>
            <div className="text-sm mt-1">{err}</div>
          </div>
        )}

        {data && (
          <>
            <h1 className="text-3xl font-extrabold font-display mt-3">{data.course_title}</h1>
            <div className="text-xs uppercase tracking-[0.2em] text-[#000000] mt-4">Awarded to</div>
            <div className="text-2xl font-bold font-display text-[#000000] mt-1" data-testid="verify-recipient">
              {data.recipient_name}
            </div>
            {data.school_name && <div className="text-sm text-[#000000]">{data.school_name}</div>}

            <div className="mt-5">
              {data.valid ? (
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[rgba(0,170,69,0.15)] border border-[#7CFFA0] text-[#7CFFA0] font-display font-bold" data-testid="verify-valid-badge">
                  <CheckCircle size={20} weight="fill" /> Certified — {data.progress_percent}% complete
                </div>
              ) : (
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[rgba(255,81,0,0.15)] border border-[#FF5100] text-[#FF5100] font-display font-bold" data-testid="verify-invalid-badge">
                  <XCircle size={20} weight="fill" /> Not yet certified — {data.progress_percent}% complete
                </div>
              )}
            </div>

            {data.valid && data.live_code && (
              <div className="mt-6 p-5 rounded-2xl bg-[#DCDEE2] border border-[#C4C8CE]">
                <div className="text-xs uppercase tracking-[0.25em] text-[#000000] mb-2 flex items-center justify-center gap-1.5">
                  <Clock size={14} /> Live verification (server-rotated)
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] uppercase text-[#000000]">Server time</div>
                    <div className="text-xl font-extrabold font-display tabular-nums text-[#000000]" data-testid="verify-live-clock">
                      {liveTime.toLocaleTimeString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-[#000000]">Live code</div>
                    <div className="text-xl font-extrabold font-mono tracking-[0.3em] text-[#CCFF00]" data-testid="verify-live-code"
                      style={{ WebkitTextStroke: "1px #000", textShadow: "0 1px 2px rgba(0,0,0,0.55)" }}>
                      {data.live_code}
                    </div>
                  </div>
                </div>
                {secsLeft != null && (
                  <div className="text-xs text-[#000000] mt-2" data-testid="verify-countdown">Rotates in {secsLeft}s</div>
                )}
                <p className="text-xs text-[#000000] mt-2 italic">Compare to the live code on the recipient's device.</p>
              </div>
            )}

            {certId && <div className="text-xs text-[#000000] font-mono mt-4">Cert ID: {certId}</div>}
          </>
        )}

        <a href="/" className="btn-ghost mt-6 inline-flex items-center gap-1 text-[#000000]" data-testid="verify-home-link">
          <ArrowLeft size={14} /> The Self-Agency Project home
        </a>
      </motion.div>
    </div>
  );
}
